import { Rule } from 'antd/es/form';

import dayjs from 'dayjs';

import { formattedPrice, validateCuil, validateWeb } from '../';

type RuleType =
  | 'alphanumeric'
  | 'alphanumericES'
  | 'cuil'
  | 'date'
  | 'dateNotAfterToday'
  | 'dateAfterToday'
  | 'email'
  | 'fullName'
  | 'integer'
  | 'notContainSpaces'
  | 'number'
  | 'phone'
  | 'required'
  | 'text'
  | 'web'
  | `max:${number}`
  | `maxLength:${number}`
  | `min:${number}`
  | `minLength:${number}`
  | `range:${number}:${number}`
  | `maxPrice:${number}`
  | `minPrice:${number}`;

export class ValidateForm {
  public generateRules({
    label,
    rules,
  }: {
    label: string;
    rules: RuleType[];
  }): Rule[] {
    const generatedRules: Rule[] = [];

    const ruleMap: { [key: string]: (label: string, value: any) => Rule } = {
      alphanumeric: () => this.validate_alphanumeric(label),
      alphanumericES: () => this.validate_alphanumericES(label),
      cuil: () => this.validate_cuil(label),
      date: () => this.validate_date(label),
      dateNotAfterToday: () => this.validate_dateNotAfterToday(label),
      dateAfterToday: () => this.validate_dateAfterToday(label),
      email: () => this.validate_email(label),
      fullName: () => this.validate_fullName(label),
      integer: () => this.validate_integer(label),
      max: (label, value) => this.validate_max(label, value),
      maxLength: (label, value) => this.validate_maxLength(label, value),
      min: (label, value) => this.validate_min(label, value[0]),
      minLength: (label, value) => this.validate_minLength(label, value),
      number: () => this.validate_number(label),
      phone: () => this.validate_phone(label),
      range: (label, value) => this.validate_range(label, value),
      required: () => this.validate_required(label),
      text: () => this.validate_text(label),
      web: () => this.validate_web(label),
      notContainSpaces: () => this.validate_notContainSpaces(label),
      maxPrice: (label, value) => this.validate_maxPrice(label, value),
      minPrice: (label, value) => this.validate_minPrice(label, value),
    };

    for (const rule of rules) {
      const [key, ...value] = rule.split(':');
      const validateFn = ruleMap[key];

      if (validateFn) {
        generatedRules.push(validateFn(label, value));
      }
    }

    return generatedRules;
  }

  private minLength(value: string, min: number): boolean {
    if (!value || (value && value.length < min)) return false;

    return true;
  }

  private validate_alphanumeric(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        let pattern = /^[a-zA-Z0-9\s]*$/;

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(`Debe ingresar un texto alfanumérico en el campo ${label}.`)
        );
      },
    };
  }

  private validate_alphanumericES(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        let pattern = /^[A-Za-zÁÉÍÓÚÜáéíóúüÑñ0-9\s.-]+$/

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(`Debe ingresar un texto alfanumérico en el campo ${label}.`)
        );
      },
    };
  }

  private validate_cuil(label: string): Rule {
    return {
      validator(_: any, value: any) {
        if (!value) return Promise.resolve();
        if (/-/.test(value)) {

          return Promise.reject(
            new Error(`El CUIL/CUIT no puede contener guiones.`)
          );
        }
       
        if (!validateCuil(value)) {
          return Promise.reject(
            new Error(`Ingrese un CUIL/CUIT válido`)
          );
        }
        return Promise.resolve();
      },
    };
  }

  private validate_date(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        if (value && new Date(value).getTime() <= 0) {
          return Promise.reject(
            new Error(`Debe ingresar una fecha en el campo ${label}.`)
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_fullName(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        const pattern =
          /^([a-zA-Z\xC0-\uFFFF]+(?:[\s.]+[a-zA-Z\xC0-\uFFFF]+)*)$/i;

        if (
          value &&
          (pattern.test(value) === false || !this.minLength(value, 2))
        ) {
          return Promise.reject(
            new Error(`Debe ingresar un nombre válido en el campo ${label}.`)
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_email(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        const pattern =
          /^[_a-zA-Z0-9-]+(\.[_a-zA-Z0-9-]+)*(\+[a-zA-Z0-9-]+)?@[a-zA-Z0-9]+[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(`Debe ingresar un email válido en el campo ${label}.`)
        );
      },
    };
  }

  private validate_integer(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        let pattern = /^-?(0|[1-9][0-9]*)$/;

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(`Debe ingresar un número entero en el campo ${label}.`)
        );
      },
    };
  }

  private validate_max(label: string, max: string): Rule {
    return {
      validator: (_: any, value: any) => {
        if (value && value > max) {
          return Promise.reject(
            new Error(
              `Debe ingresar un número menor a ${max} en el campo ${label}.`
            )
          );
        }
        return Promise.resolve();
      },
    };
  }

  private validate_maxLength(label: string, max: string): Rule {
    return {
      validator(_: any, value: any) {
        if (value && value.length > max) {
          return Promise.reject(
            new Error(
              `Debe ingresar un máximo de ${max} caracteres en el campo ${label}.`
            )
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_min(label: string, min: string): Rule {
    return {
      validator: (_: any, value: number) => {
        if (value !== undefined && value !== null && value < Number(min)) {
          return Promise.reject(
            new Error(
              `Debe ingresar un número mayor o igual a ${min} en el campo ${label}.`
            )
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_minLength(label: string, min: string): Rule {
    return {
      validator(_: any, value: any) {
        if (value && value.length < min) {
          return Promise.reject(
            new Error(
              `Debe ingresar un mínimo de ${min} caracteres.`
            )
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_number(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        if (value && isNaN(value) === true) {
          return Promise.reject(
            new Error(`Debe ingresar un número válido en el campo ${label}.`)
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_phone(label: string): Rule {
    return {
      validator(_: any, value: any) {
        const pattern = new RegExp(/^[0-9]*$/);

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(
            `Debe ingresar un número de teléfono válido en el campo ${label}.`
          )
        );
      },
    };
  }

  private validate_range(label: string, value: any): Rule {
    const from = value[0];
    const to = value[1];
    
    return {
      validator(_: any, inputValue: any) {
        const num = Number(inputValue);
  
        if ((inputValue === '' || inputValue === null || inputValue === undefined) ||
            (num >= from && num <= to)) {
          return Promise.resolve();
        }
  
        return Promise.reject(
          new Error(
            `Debe ingresar un valor entre ${formattedPrice(from)} y ${formattedPrice(to)} en el campo ${label}.`
          )
        );
      },
    }
  }

  private validate_required(label: string): Rule {
    return {
      required: true,
      message: `El campo ${label} es requerido.`,
    };
  }

  private validate_text(label: string): Rule {
    return {
      validator: (_: any, value: any) => {
        const pattern = /^[a-zA-ZáÁéÉíÍóÓúÚñÑ'´]*$/;

        if (!value || pattern.test(value)) return Promise.resolve();

        return Promise.reject(
          new Error(`Debe ingresar un texto válido en el campo ${label}.`)
        );
      },
    };
  }

  private validate_web(label: string): Rule {
    return {
      validator(_: any, value: any) {
        if (!value || validateWeb(value)) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error(`Debe ingresar una web valida en el campo ${label}.`)
        );
      },
    };
  }

  private validate_dateNotAfterToday(label: string): Rule {
    return {
      validator(_: any, value: any) {
        const today = dayjs().startOf('day');

        if (
          !value ||
          value.isSame(today, 'day') ||
          value.isBefore(today, 'day')
        ) {
          return Promise.resolve();
        }

        return Promise.reject(
          new Error(`La fecha del campo ${label} no puede ser posterior a hoy.`)
        );
      },
    };
  }

  validate_dateAfterToday(label: string): Rule {
    return {
      validator(_, value) {
        if (!value) return Promise.resolve();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
  
        const selectedDate = new Date(value);
        if (selectedDate > today) {
          return Promise.resolve();
        }
        return Promise.reject(`${label} debe ser posterior a hoy`);
      },
    };
  }  

  private validate_notContainSpaces(label: string): Rule {
    return {
      validator(_: any, value: string) {
        if (!value) {
          return Promise.resolve();
        }

        const containsSpaces = /\s/.test(value);
        if (containsSpaces) {
          return Promise.reject(
            new Error(`El campo ${label} no puede contener espacios.`)
          );
        }

        return Promise.resolve();
      },
    };
  }

  private validate_maxPrice(label: string, max: string): Rule {
    return {
      validator: (_: any, value: any) => {
        if (value && value > max) {
          return Promise.reject(
            new Error(
              `Debe ingresar un número menor a $${formattedPrice(
                max
              )} en el campo ${label}.`
            )
          );
        }
        return Promise.resolve();
      },
    };
  }

  private validate_minPrice(label: string, min: string): Rule {
    return {
      validator: (_: any, value: any) => {
        if (value && value < min) {
          return Promise.reject(
            new Error(
              `Debe ingresar un número mayor a $${formattedPrice(
                min
              )} en el campo ${label}.`
            )
          );
        }

        return Promise.resolve();
      },
    };
  }
}
